/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import {
  MITARBEITER_THRESHOLD,
  STUNDEN_MAX,
} from '@/domain/dienstplan/features/VisualEditor/constants';
import { endBelegungCreate } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/endBelegungCreate';
import { endBelegungResize } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/endBelegungResize';
import { getMousePosition } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/utils/getMousePosition';
import { getSnappedMousePosition } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/utils/getSnappedMousePosition';
import { SharedContext } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/types';
import { Point } from '@/domain/dienstplan/features/VisualEditor/types';
import { isMouseIntersectInterval } from '@/domain/dienstplan/features/VisualEditor/utils/mouseHelper/isMouseIntersectInterval';
import {
  Belegung,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';

export function updateBelegung({
  ctx,
  currentPoint,
}: {
  ctx: SharedContext;
  currentPoint: Point;
}) {
  const {
    belegungen,
    store: {
      ui: { startBelegungPosition, isResizingStart, isResizingEnde },
      updateBelegungById,
      removeBelegungById,
      setIsPopoverOpen,
      setIsResizingEnde,
      setIsResizingStart,
      setStartBelegungPosition,
      setHoveredBelegung,
      setPopoverPosition,
    },
    maxMitarbeiterAnzahl,
    deleteBelegung,
  } = ctx;

  if (!startBelegungPosition) {
    return;
  }

  const currentSnappedPosition = getSnappedMousePosition(ctx, currentPoint);
  const currentPosition = getMousePosition(ctx, currentPoint);

  const activeBelegung =
    isResizingStart || isResizingEnde
      ? belegungen.find(
          (b) => b.mode === 'resize' || b.mode === 'resize-height',
        )
      : belegungen.find((b) => b.mode === 'active');

  if (!activeBelegung) return;

  // Prüfe Überschneidungen
  const otherBelegungen = getOtherBelegungenInBereich(ctx);
  const intersects = isMouseIntersectInterval(
    otherBelegungen,
    currentPosition,
    startBelegungPosition,
  );

  if (intersects.isIntersect) {
    if (isResizingEnde || isResizingStart) {
      endBelegungResize({ ctx, collision: intersects });
      return;
    }
    endBelegungCreate({ ctx, collision: intersects });
    return;
  }

  // Prüfe ob sich der Cursor außerhalb des Koordinatensystems befindet
  if (
    currentPosition.x >= STUNDEN_MAX ||
    currentPosition.x <= 0 ||
    currentPosition.y >= maxMitarbeiterAnzahl + MITARBEITER_THRESHOLD
  ) {
    if (isResizingStart || isResizingEnde) {
      endBelegungResize({ ctx });
      return;
    }
    endBelegungCreate({ ctx });
    return;
  }

  if (activeBelegung.mode === 'resize-height') {
    const updateBelegung = createUpdatedBelegung(activeBelegung, {
      y: currentSnappedPosition.y,
      start: activeBelegung.startX,
      ende: activeBelegung.endeX,
    });
    const newIntervall = new BelegungsIntervall(
      updateBelegung,
      'resize-height',
    );
    ctx.store.updateBelegungById(newIntervall, newIntervall.belegung.id);
    return;
  }

  if (isResizingStart || isResizingEnde) {
    let start = isResizingStart
      ? currentSnappedPosition.x
      : activeBelegung.belegung.start;

    let ende = isResizingEnde
      ? currentSnappedPosition.x
      : activeBelegung.belegung.ende;

    if (
      isResizingEnde &&
      currentSnappedPosition.x <= activeBelegung.belegung.start
    ) {
      start = currentSnappedPosition.x;
      ende = activeBelegung.belegung.start;
      ctx.store.setIsResizingEnde(false);
      ctx.store.setIsResizingStart(true);
    }
    if (
      isResizingStart &&
      currentSnappedPosition.x >= activeBelegung.belegung.ende
    ) {
      ende = currentSnappedPosition.x;
      start = activeBelegung.belegung.ende;
      ctx.store.setIsResizingEnde(true);
      ctx.store.setIsResizingStart(false);
    }

    const updatedBelegung = createUpdatedBelegung(activeBelegung, {
      y: activeBelegung.belegung.personenanzahlMax,
      start,
      ende,
    });

    const newInterval = new BelegungsIntervall(updatedBelegung, 'resize');
    if (start === 0 && ende === 0) {
      deleteBelegung({ belegungId: updatedBelegung.id }).then(() => {
        removeBelegungById(updatedBelegung.id);
        setStartBelegungPosition(undefined);
        setIsResizingStart(false);
        setIsResizingEnde(false);
        setPopoverPosition(undefined);
        setHoveredBelegung(undefined);
        setIsPopoverOpen(false);
      });
    } else {
      updateBelegungById(newInterval, newInterval.belegung.id);
    }
  } else {
    const isLeftDrag = startBelegungPosition.x > currentSnappedPosition.x;

    const updatedBelegung = createUpdatedBelegung(activeBelegung, {
      y: currentSnappedPosition.y,
      start: isLeftDrag ? currentSnappedPosition.x : startBelegungPosition.x,
      ende: isLeftDrag ? startBelegungPosition.x : currentSnappedPosition.x,
    });

    const newInterval = new BelegungsIntervall(updatedBelegung, 'active');
    ctx.store.updateBelegungById(newInterval, newInterval.belegung.id);
  }
}

function getOtherBelegungenInBereich(ctx: SharedContext) {
  return ctx.belegungen
    .filter((b) => b.mode !== 'active' && b.mode !== 'resize')
    .filter(
      (b) => b.belegung.arbeitsbereich.id === ctx.selectedArbeitsbereich.id,
    );
}

function createUpdatedBelegung(
  active: BelegungsIntervall,
  { y, start, ende }: { y: number; start: number; ende: number },
) {
  return new Belegung({
    ...active.belegung,
    personenanzahlMax: y,
    start,
    ende,
  });
}
